import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PageHero } from '@/components/generals/page-hero'
import { CtaPanel } from '@/components/generals/cta-panel'
import { Reveal } from '@/components/generals/reveal'
import { Blueprint, CreamSheet, DarkBand } from '@/components/generals/dark-band'
import { ContactIntake } from '@/components/contact/contact-intake'
import { ContactCards } from '@/components/contact/contact-cards'
import { CONTACT_TOPICS, ContactForm } from '@/components/contact/contact-form'

const TOPIC_PRESETS: Record<string, string> = {
    connection: "A new connection",
    support: "Support for a live flow",
    partner: "Partnership",
    pilot: "Pilot",
    api: "Open API"
}

const NEXT_STEPS = [
    "A solutions engineer reads your answers the same day.",
    "You get a reply within one working day, from a person.",
    "If it is a fit, we scope your first flow together on a call."
]

const Contact: FC = (): ReactNode => {
    const router = useRouter()
    const formRef = useRef<HTMLDivElement>(null)
    const [formOpen, setFormOpen] = useState(false)
    const [topic, setTopic] = useState(CONTACT_TOPICS[0])
    const [initialMessage, setInitialMessage] = useState('')

    const openForm = (preset?: string) => {
        if (preset) setTopic(preset)

        setFormOpen(true)
        window.setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80)
    }

    useEffect(() => {
        if (!router.isReady) return

        const topicKey = typeof router.query.topic === 'string' ? router.query.topic : ''
        const system = typeof router.query.system === 'string' ? router.query.system.slice(0, 120) : ''
        const preset = TOPIC_PRESETS[topicKey]

        if (system) setInitialMessage(`We need a connection to ${system}.`)
        if (preset) openForm(preset)
    }, [router.isReady, router.query.topic, router.query.system])

    return (
        <Layout>
            <Seo
                title="Contact"
                description="Tell us what is stuck. Answer a few quick questions and a person from the Quayside solutions team replies within one working day."
            />

            <PageHero
                label="Contact"
                titleTop="Tell us"
                titleBottom="what's stuck."
                description="Answer a few quick questions and a person from our solutions team will reply within one working day. Prefer to talk? The numbers are below."
            />

            <DarkBand id='intake' aria-labelledby='intake-title' className='band-y'>
                <div className='shell relative grid gap-12 sm:gap-16 lg:grid-cols-12 lg:gap-12'>
                    <Reveal className='lg:col-span-5'>
                        <p className='eyebrow flex items-center gap-2.5 text-white/50'>
                            <span aria-hidden='true' className='size-1.5 rounded-full bg-ultramarine' />
                            Start here
                        </p>

                        <h2 id='intake-title' className='headline mt-6 text-[clamp(2.25rem,5vw,4.3rem)]'>
                            <span className='block text-white'>
                                A conversation,
                            </span>

                            <span className='text-aurora'>
                                not a ticket.
                            </span>
                        </h2>

                        <p className='mt-7 max-w-md text-lg leading-relaxed text-mist'>
                            Tell us what you ship and what needs to connect. Your answers go straight to a solutions engineer, not a queue.
                        </p>

                        <ol className='mt-10 space-y-5'>
                            {NEXT_STEPS.map((step, index) => (
                                <li key={step} className='flex gap-4'>
                                    <span className='font-mono text-[12px] text-white/40'>
                                        {`0${index + 1}`}
                                    </span>

                                    <span className='text-[15px] leading-relaxed text-white/80'>
                                        {step}
                                    </span>
                                </li>
                            ))}
                        </ol>
                    </Reveal>

                    <div className='relative min-w-0 lg:col-span-7 lg:pl-6 xl:pl-12'>
                        <Blueprint className='-inset-x-4 -inset-y-12 sm:-inset-x-10' />

                        <Reveal delay={140} className='relative'>
                            <ContactIntake />
                        </Reveal>
                    </div>
                </div>
            </DarkBand>

            <CreamSheet aria-label='Other ways to reach us'>
                <div className='shell section-y'>
                    <ContactCards onOpenSupport={() => openForm(TOPIC_PRESETS.support)} />

                    <div ref={formRef} id='contact-form' className='mt-6 scroll-mt-28'>
                        <ContactForm
                            open={formOpen}
                            onOpenChange={setFormOpen}
                            topic={topic}
                            onTopicChange={setTopic}
                            initialMessage={initialMessage}
                        />
                    </div>
                </div>

                <CtaPanel
                    title="Want to see it first?"
                    accent="Explore the platform."
                    primary={{ label: "See the platform", href: '/platform' }}
                    secondary={{ label: "Browse the network", href: '/network' }}
                />
            </CreamSheet>
        </Layout>
    )
}

export default Contact
